// Otter history import hooks (openapi `imports` tag; docs/specs/otter-import-1.md).
//
// The backend 404s every /imports/otter route unless the feature flag is on, so
// `useOtterImportStatus` resolves to `null` on a 404 — "feature unavailable" — and callers
// hide the entry point. Mock mode (`dev:mock`) has no Otter to talk to, so it is always
// unavailable there too.
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ScribeApiError } from '@scribe/core/client';
import type { OtterImportRun, OtterImportStatus } from '@scribe/core/types';
import { useApi } from './ApiContext';
import { isMock } from './mock';

export const OTTER_STATUS_KEY = ['otter-import-status'] as const;
const RUN_POLL_MS = 2000;

export function isActiveRun(run: OtterImportRun | null | undefined): boolean {
  return run?.status === 'queued' || run?.status === 'running';
}

export function useOtterImportStatus() {
  const { client } = useApi();
  return useQuery<OtterImportStatus | null>({
    queryKey: OTTER_STATUS_KEY,
    queryFn: async () => {
      if (isMock()) return null;
      try {
        return await client.getOtterImportStatus();
      } catch (err) {
        if (err instanceof ScribeApiError && err.status === 404) return null;
        throw err;
      }
    },
    staleTime: 60_000,
    retry: false,
  });
}

/** Polls a run while it is queued/running; stops once it reaches a terminal status. */
export function useOtterImportRun(runId: string | undefined) {
  const { client } = useApi();
  const qc = useQueryClient();
  return useQuery<OtterImportRun>({
    queryKey: ['otter-import-run', runId],
    enabled: !!runId,
    queryFn: async () => {
      const run = await client.getOtterImportRun(runId as string);
      if (!isActiveRun(run)) void qc.invalidateQueries({ queryKey: ['recordings'] });
      return run;
    },
    refetchInterval: (query) => (isActiveRun(query.state.data) ? RUN_POLL_MS : false),
  });
}

export function useConnectOtter() {
  const { client } = useApi();
  return useMutation({ mutationFn: () => client.connectOtter() });
}

export function useCompleteOtterConnect() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    // One click: connecting starts the history import straight away (no separate Start).
    mutationFn: (body: { code: string; state: string }) =>
      client.completeOtterConnect({ ...body, start_import: true }),
    onSuccess: (status) => {
      qc.setQueryData(OTTER_STATUS_KEY, status);
      if (status.latest_run) qc.setQueryData(['otter-import-run', status.latest_run.id], status.latest_run);
    },
  });
}

export function useStartOtterImport() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => client.startOtterImport(),
    onSuccess: (run) => {
      qc.setQueryData(['otter-import-run', run.id], run);
      void qc.invalidateQueries({ queryKey: OTTER_STATUS_KEY });
    },
  });
}

export function useDisconnectOtter() {
  const { client } = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => client.disconnectOtter(),
    onSuccess: () => void qc.invalidateQueries({ queryKey: OTTER_STATUS_KEY }),
  });
}

/** Reads (and strips) the OAuth redirect params Otter appended to our callback URL. */
export function takeOtterCallbackParams():
  { code: string; state: string } | { error: string } | null {
  const params = new URLSearchParams(window.location.search);
  const code = params.get('code');
  const state = params.get('state');
  const error = params.get('error');
  if (!code && !error) return null;
  // Never leave the one-time code in the address bar / history.
  window.history.replaceState(null, '', '/import');
  if (error || !code || !state) return { error: error ?? 'missing_code' };
  return { code, state };
}
